import cn from 'clsx'
import React, { useEffect } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'

import { horizontalChildSpacing, verticalChildSpacing } from 'components/utils/spacing'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { mediaQueryUp, mediaQueryDown } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import config from 'constants/config'

import { AccountAccessModalResult } from './AccountAccessModal'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import { LAST_USED_LOGIN_METHOD_LABEL, LastUsedLoginMethod } from 'lib/auth/lastUsedLoginMethod'
import TextButton from 'LuxKit/Button/TextButton'
import SolidEnvelopeAltIcon from 'LuxKit/Icons/solid/SolidEnvelopeAltIcon'
import SolidPhoneIcon from 'LuxKit/Icons/solid/SolidPhoneIcon'
import SocialLoginButton, { SOCIAL_LOGIN_TEXT_BUTTON_PROPS } from './SocialLoginButton/SocialLoginButton'

const SocialContainer = styled.div`
  margin-bottom: ${rem(48)};

  ${verticalChildSpacing[12]}

  &.inline {
    display: flex;
    width: 100%;
    flex-wrap: nowrap;

    > * {
      flex: 1;
    }

    ${verticalChildSpacing[0]};
    ${horizontalChildSpacing[12]};
  }

  ${mediaQueryDown.mobile} {
    &.gridOnMobile {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: ${rem(12)};
      row-gap: ${rem(20)};

      &.inline {
        display: grid;
        width: 100%;

        > * {
          flex: unset;
          margin: 0;
        }
      }
    }
  }

  ${mediaQueryUp.tablet} {
    margin-bottom: 0;
  }
`

const AppLinks = styled(ClickableLink)`
  display: block;
`

const Img = styled(Image)`
  max-width: 100%;
`

const SocialButtonWrapper = styled.div`
  position: relative;
  width: 100%;

  ${mediaQueryDown.mobile} {
    &.gridOnMobile {
      width: 100%;
      margin: 0;
    }
  }
`

const LastUsedTag = styled(StatusLabel)`
  position: absolute;
  top: 0;
  transform: translateY(-67%);
  right: 0;
  pointer-events: none;
  z-index: 1;
`

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(8)};
`

interface Props {
  mode: 'login' | 'join'
  isBlocked?: boolean
  hideText?: boolean
  inline?: boolean
  classNameFacebook?: string
  classNameGoogle?: string
  onPrivateAppleEmailLogin?: (appleLoginData: App.AppleLoginData, email: string) => void
  lastUsedLoginMethod?: LastUsedLoginMethod
  onEmailEntryClick?: () => void
  onPhoneEntryClick?: () => void
  gridOnMobile?: boolean
}

/**
 * Determines if we're currently in the facebook apps internal browser.
 *
 * It can sometimes have considerably different behaviour compared regular web browsers.
 * @returns Whether or not we're in the facebook app browser
 */
function isFacebookApp() {
  if (IS_SSR) {
    return false
  }

  const ua = window.navigator?.userAgent || window.navigator?.vendor || window?.opera
  return (ua.indexOf('FBAN') > -1) || (ua.indexOf('FBAV') > -1)
}

function AccountAccessSocialButtons(props: Props) {
  const {
    isBlocked,
    classNameFacebook,
    classNameGoogle,
    mode,
    hideText,
    inline,
    onPrivateAppleEmailLogin,
    lastUsedLoginMethod,
    onEmailEntryClick,
    onPhoneEntryClick,
    gridOnMobile = false,
  } = props

  const isFBApp = isFacebookApp()

  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const loggedIn = useAppSelector(selectLoggedIn)

  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  const shouldShowGoogle = Boolean(config.GOOGLE_APP_ID)
  const shouldShowApple = Boolean(config.APPLE_APP_ID)
  const shouldShowFacebook = Boolean(config.FACEBOOK_APP_ID)

  const emailButton = onEmailEntryClick ? (
    <SocialButtonWrapper key="email" className={cn({ gridOnMobile })}>
      {lastUsedLoginMethod === 'email' && <LastUsedTag variant="confirmation">{LAST_USED_LOGIN_METHOD_LABEL}</LastUsedTag>}
      <TextButton
        {...SOCIAL_LOGIN_TEXT_BUTTON_PROPS}
        type="button"
        onClick={onEmailEntryClick}
      >
        <ButtonContent>
          <SolidEnvelopeAltIcon width={18} height={18} />
          Email
        </ButtonContent>
      </TextButton>
    </SocialButtonWrapper>
  ) : null

  const phoneButton = onPhoneEntryClick ? (
    <SocialButtonWrapper key="phone" className={cn({ gridOnMobile })}>
      {(lastUsedLoginMethod === 'phone' || lastUsedLoginMethod === 'otp') && <LastUsedTag variant="confirmation">{LAST_USED_LOGIN_METHOD_LABEL}</LastUsedTag>}
      <TextButton
        {...SOCIAL_LOGIN_TEXT_BUTTON_PROPS}
        type="button"
        onClick={onPhoneEntryClick}
      >
        <ButtonContent>
          <SolidPhoneIcon width={18} height={18} />
          Phone
        </ButtonContent>
      </TextButton>
    </SocialButtonWrapper>
  ) : null

  const googleButton = shouldShowGoogle ? (
    <SocialButtonWrapper key="google" className={cn({ gridOnMobile })}>
      {lastUsedLoginMethod === 'google' && <LastUsedTag variant="confirmation">{LAST_USED_LOGIN_METHOD_LABEL}</LastUsedTag>}
      <SocialLoginButton
        mode={mode}
        isBlocked={isBlocked}
        className={classNameGoogle}
        source="google"
        iconOnly={hideText}
      />
    </SocialButtonWrapper>
  ) : null

  const appleButton = shouldShowApple ? (
    <SocialButtonWrapper key="apple" className={cn({ gridOnMobile })}>
      {lastUsedLoginMethod === 'apple' && <LastUsedTag variant="confirmation">{LAST_USED_LOGIN_METHOD_LABEL}</LastUsedTag>}
      <SocialLoginButton
        mode={mode}
        isBlocked={isBlocked}
        className={classNameGoogle}
        source="apple"
        iconOnly={hideText}
        onPrivateAppleEmailLogin={onPrivateAppleEmailLogin}
      />
    </SocialButtonWrapper>
  ) : null

  const facebookButton = shouldShowFacebook ? (
    <SocialButtonWrapper key="facebook" className={cn({ gridOnMobile })}>
      {lastUsedLoginMethod === 'facebook' && <LastUsedTag variant="confirmation">{LAST_USED_LOGIN_METHOD_LABEL}</LastUsedTag>}
      <SocialLoginButton
        mode={mode}
        isBlocked={isBlocked}
        className={classNameFacebook}
        source="facebook"
        iconOnly={hideText}
      />
    </SocialButtonWrapper>
  ) : null

  const orderedButtons = (gridOnMobile && onEmailEntryClick)
    ? [emailButton, googleButton, appleButton, phoneButton, facebookButton]
    : [emailButton, phoneButton, googleButton, appleButton, facebookButton]

  return (
    <SocialContainer className={cn({ inline, gridOnMobile })}>
      {isFBApp && (
        <Group direction="horizontal" horizontalAlign="center" gap={8}>
          <AppLinks href="https://app.luxuryescapes.com/3WHB/1aab092" target="_blank" rel="noopener">
            <Img id="App_Store_suxzv1" alt="Download the iPhone app" width={158} />
          </AppLinks>
          <AppLinks href="https://app.luxuryescapes.com/3WHB/feb04c69" target="_blank" rel="noopener">
            <Img id="Google_Play_o1xn2t" alt="Download the Android app" width={158} />
          </AppLinks>
        </Group>
      )}
      {!isFBApp && (
        <ErrorBoundary fallback={() => null} reportErrors>
          {orderedButtons.filter(Boolean)}
        </ErrorBoundary>
      )}
    </SocialContainer>
  )
}

export default AccountAccessSocialButtons
